import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

// Handles the return from Emergent Google OAuth: exchanges the session_id (in the URL
// fragment) for our session, then lands on the dashboard.
export default function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return; // guard against StrictMode double-run
    processed.current = true;

    const m = window.location.hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    api
      .createSession(sid)
      .then(({ user }) => {
        // The backend's /auth/session response sets the httpOnly session cookie; nothing to store client-side.
        // Fresh Google login clears any App Lock lockout (the passcode-recovery path).
        api.resetAppLock().catch(() => {});
        setUser(user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      })
      .catch((err) => {
        navigate("/login", { replace: true, state: { error: formatApiError(err.response?.data?.detail) || "Sign-in failed." } });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3 text-zinc-300">
        <Loader2 className="h-6 w-6 animate-spin text-[#E0575B]" />
        <p className="text-sm">Signing you in…</p>
      </div>
    </div>
  );
}
